import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader';
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader';
import { VignetteShader } from 'three/examples/jsm/shaders/VignetteShader';
import { BrightnessContrastShader } from 'three/examples/jsm/shaders/BrightnessContrastShader';
import { HueSaturationShader } from 'three/examples/jsm/shaders/HueSaturationShader';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';

const defaultSegments = [
  { start: 0, points: 128, ringCount: 360, speed: 0.5, lineWidth: 1, ringAmpScale: 1, amplitude: 1, shape: 'circle', randomness: 0, rotationSpeed: 0, zoomMin: 2, zoomMax: 120, zoomStart: 40 },
];

const qualityProfiles = {
  high: { pixelRatioCap: 2, postFX: true, frameSkip: 1, fftSize: 1024 },
  medium: { pixelRatioCap: 1.5, postFX: true, frameSkip: 1, fftSize: 512 },
  low: { pixelRatioCap: 1, postFX: false, frameSkip: 1, fftSize: 512 },
};

const getQualityProfile = (tier) => qualityProfiles[tier] || qualityProfiles.high;

const clampValue = (value, min, max, fallback = min) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
};

const clampSettings = (source) => {
  const s = source || {};
  return {
    points: Math.round(clampValue(s.points, 32, 192, 128)),
    ringCount: Math.round(clampValue(s.ringCount, 100, 480, 360)),
    lineWidth: clampValue(s.lineWidth, 0.5, 12, 1),
    ringAmpScale: clampValue(s.ringAmpScale, 0.2, 4, 1),
    amplitude: clampValue(s.amplitude, 0, 6, 1),
    shape: s.shape || 'circle',
    randomness: clampValue(s.randomness, 0, 0.6, 0),
    rotationSpeed: clampValue(s.rotationSpeed ?? s.rotationZ, -6, 6, 0),
    zoomMin: 1,
    zoomMax: 300,
    originX: clampValue(s.originX, -20, 20, 0),
    originY: clampValue(s.originY, -20, 20, 0),
    centerYOffset: clampValue(s.centerYOffset, -240, 240, 0),
  };
};

const buildSegments = (scene) => {
  if (!scene) return defaultSegments;
  const settings = clampSettings(scene.settings || {});
  const frames = Array.isArray(scene.keyframes) ? [...scene.keyframes] : [];
  if (!frames.length) {
    return [{
      start: 0,
      points: settings.points || 128,
      ringCount: settings.ringCount || 360,
      speed: 1,
      lineWidth: settings.lineWidth ?? 1,
      ringAmpScale: settings.ringAmpScale ?? 1,
      amplitude: settings.amplitude ?? 1,
      shape: settings.shape || 'circle',
      randomness: settings.randomness ?? 0,
      rotationSpeed: settings.rotationSpeed ?? settings.rotationZ ?? 0,
      zoomMin: 1,
      zoomMax: 300,
      zoomStart: 40,
      zoomEnd: 40,
      blurStart: 0,
      blurEnd: 0,
      brightnessStart: 0,
      brightnessEnd: 0,
      originStart: { x: settings.originX ?? 0, y: settings.originY ?? 0 },
      originEnd: { x: settings.originX ?? 0, y: settings.originY ?? 0 },
      centerYOffsetStart: settings.centerYOffset ?? 0,
      centerYOffsetEnd: settings.centerYOffset ?? 0,
    }];
  }
  frames.sort((a, b) => (a.time || 0) - (b.time || 0));
  return frames.map((kf, idx) => {
    const next = frames[idx + 1] || kf;
    return {
      start: Number(kf.time) || 0,
      points: clampValue(kf.points ?? settings.points ?? 128, 32, 192, 128),
      ringCount: clampValue(kf.ringCount ?? settings.ringCount ?? 360, 100, 480, 360),
      speed: kf.speed ?? 1,
      lineWidth: clampValue(kf.lineWidth ?? settings.lineWidth ?? 1, 0.5, 12, 1),
      ringAmpScale: clampValue(kf.ringAmpScale ?? settings.ringAmpScale ?? 1, 0.2, 4, 1),
      amplitude: clampValue(kf.amplitude ?? settings.amplitude ?? 1, 0, 6, 1),
      shape: kf.shape || settings.shape || 'circle',
      randomness: clampValue(kf.randomness ?? settings.randomness ?? 0, 0, 0.6, 0),
      rotationSpeed: clampValue(kf.rotationSpeed ?? kf.rotationZ ?? settings.rotationSpeed ?? settings.rotationZ ?? 0, -6, 6, 0),
      zoomMin: 1,
      zoomMax: 300,
      zoomStart: kf.zoom ?? 40,
      zoomEnd: next.zoom ?? kf.zoom ?? 40,
      blurStart: kf.blur ?? 0,
      blurEnd: next.blur ?? kf.blur ?? 0,
      brightnessStart: kf.brightness ?? 0,
      brightnessEnd: next.brightness ?? kf.brightness ?? 0,
      originStart: {
        x: clampValue(kf.originX ?? settings.originX ?? 0, -20, 20, 0),
        y: clampValue(kf.originY ?? settings.originY ?? 0, -20, 20, 0),
      },
      originEnd: {
        x: clampValue(next.originX ?? kf.originX ?? settings.originX ?? 0, -20, 20, 0),
        y: clampValue(next.originY ?? kf.originY ?? settings.originY ?? 0, -20, 20, 0),
      },
      centerYOffsetStart: clampValue(kf.centerYOffset ?? settings.centerYOffset ?? 0, -240, 240, 0),
      centerYOffsetEnd: clampValue(next.centerYOffset ?? kf.centerYOffset ?? settings.centerYOffset ?? 0, -240, 240, 0),
    };
  });
};

const polygonRadius = (angle, sides) => {
  const segment = (Math.PI * 2) / sides;
  const offset = ((angle + Math.PI) % (Math.PI * 2)) % segment - segment / 2;
  const denom = Math.cos(offset);
  return Math.cos(Math.PI / sides) / Math.max(0.2, denom);
};

const shapeRadius = (angle, shape, amp, wave) => {
  const applyWave = (r) => r * (1 + wave * amp * 0.2);
  if (shape === 'polygon' || shape === 'hexagon') {
    return polygonRadius(angle, 6);
  }
  if (shape === 'triangle') {
    return polygonRadius(angle, 3);
  }
  if (shape === 'square') {
    return polygonRadius(angle, 4);
  }
  if (shape === 'star') {
    const spikes = 5;
    const inner = 0.55;
    const outer = 1;
    const t = (angle * spikes) / (Math.PI * 2);
    const frac = t - Math.floor(t);
    const radius = frac < 0.5
      ? outer - (outer - inner) * (frac / 0.5)
      : inner + (outer - inner) * ((frac - 0.5) / 0.5);
    return applyWave(radius);
  }
  if (shape === 'flower') {
    return applyWave(1 + Math.sin(angle * 6) * 0.3);
  }
  return applyWave(1);
};

const MotionScaleMin = 0.1;
const MotionScaleMax = 1;
const DefaultMotionTransitionSeconds = 2;

const MusicVisualizer = ({
  audio,
  currentTime = 0,
  progress = 0,
  scene,
  active = true,
  targetFps = 60,
  motionTarget = 1,
  motionTransitionSeconds = DefaultMotionTransitionSeconds,
  deferAnalyserMs = 0,
}) => {
  const containerRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const dataRef = useRef(null);
  const freqRef = useRef(null);
  const analyserReadyAtRef = useRef(0);
  const currentTimeRef = useRef(0);
  const zoomOffsetRef = useRef(0);
  const targetFpsRef = useRef(60);
  const motionTransitionSecondsRef = useRef(
    clampValue(motionTransitionSeconds, 0.05, 10, DefaultMotionTransitionSeconds)
  );
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [qualityTier, setQualityTier] = useState('high');
  const qualityTierRef = useRef('high');
  const perfRef = useRef({ samples: new Array(30).fill(1 / 60), idx: 0, lastEval: 0, lastChange: 0 });
  const qualityRuntimeRef = useRef({ lastTier: 'high', lastPixelRatioCap: 0, frame: 0 });
  const motionTargetRef = useRef(clampValue(motionTarget, MotionScaleMin, MotionScaleMax, MotionScaleMax));
  const motionScaleRef = useRef(clampValue(motionTarget, MotionScaleMin, MotionScaleMax, MotionScaleMax));

  const segments = useMemo(() => buildSegments(scene), [scene]);

  useEffect(() => {
    currentTimeRef.current = isFinite(currentTime) ? currentTime : 0;
  }, [currentTime]);

  useEffect(() => {
    targetFpsRef.current = Number.isFinite(targetFps) && targetFps > 0 ? targetFps : 60;
  }, [targetFps]);

  useEffect(() => {
    const list = [...(segments || [])].sort((a, b) => a.start - b.start);
    let nextIndex = 0;
    for (let i = 0; i < list.length; i += 1) {
      if (currentTimeRef.current >= list[i].start) nextIndex = i;
    }
    if (nextIndex !== segmentIndex) setSegmentIndex(nextIndex);
  }, [segments, segmentIndex, currentTime]);

  useEffect(() => {
    setSegmentIndex(0);
  }, [segments]);

  useEffect(() => {
    qualityTierRef.current = qualityTier;
  }, [qualityTier]);

  useEffect(() => {
    motionTargetRef.current = clampValue(motionTarget, MotionScaleMin, MotionScaleMax, MotionScaleMax);
  }, [motionTarget]);

  useEffect(() => {
    motionTransitionSecondsRef.current = clampValue(
      motionTransitionSeconds,
      0.05,
      10,
      DefaultMotionTransitionSeconds
    );
  }, [motionTransitionSeconds]);

  useEffect(() => {
    const waitMs = clampValue(deferAnalyserMs, 0, 15000, 0);
    analyserReadyAtRef.current = waitMs > 0 ? performance.now() + waitMs : 0;
  }, [deferAnalyserMs, audio]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getSize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      return {
        width: w > 0 ? w : Math.min(900, window.innerWidth || 900),
        height: h > 0 ? h : 520,
      };
    };

    const initialQuality = getQualityProfile(qualityTierRef.current);
    const initialSize = getSize();
    const width = initialSize.width;
    const height = initialSize.height;

    const scene3d = new THREE.Scene();
    scene3d.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    const segmentsList = (segments && segments.length ? segments : defaultSegments)
      .slice()
      .sort((a, b) => a.start - b.start);
    const activeSegment = segmentsList[Math.min(segmentIndex, segmentsList.length - 1)] ?? segmentsList[0];
    const nextSegment = segmentsList[Math.min(segmentIndex + 1, segmentsList.length - 1)] ?? activeSegment;
    const points = activeSegment.points || 128;
    const ringCount = activeSegment.ringCount || 360;
    const baseLineWidth = activeSegment.lineWidth ?? 1;
    const baseRingAmpScale = activeSegment.ringAmpScale ?? 1;
    const baseAmplitudeScale = activeSegment.amplitude ?? 1;
    const shape = activeSegment.shape || 'circle';
    const baseRandomness = activeSegment.randomness ?? 0;
    const baseSpeed = activeSegment.speed ?? 0;

    const minZoom = activeSegment.zoomMin ?? 2;
    const maxZoom = activeSegment.zoomMax ?? 120;
    const camStart = activeSegment.cameraStart ?? { x: 0, y: 0 };
    const camEnd = activeSegment.cameraEnd ?? camStart;
    const rotStart = activeSegment.rotationStart ?? { x: 0, y: 0, z: 0 };
    const rotEnd = activeSegment.rotationEnd ?? rotStart;
    const baseRotationSpeed = activeSegment.rotationSpeed ?? 0;
    const originStart = activeSegment.originStart ?? { x: 0, y: 0 };
    const originEnd = activeSegment.originEnd ?? originStart;
    const startZoom = activeSegment.zoomStart ?? camStart.z ?? maxZoom;
    const endZoom = activeSegment.zoomEnd ?? camEnd.z ?? startZoom;

    const segmentEndTime = (() => {
      const nextSeg = segmentsList[Math.min(segmentIndex + 1, segmentsList.length - 1)];
      if (nextSeg && nextSeg.start > activeSegment.start) return nextSeg.start;
      const duration = audio?.duration;
      if (duration && isFinite(duration) && duration > activeSegment.start) return duration;
      return activeSegment.start + 10;
    })();
    const getSegmentT = () => {
      const segStart = activeSegment.start ?? 0;
      const segDuration = Math.max(0.001, segmentEndTime - segStart);
      return Math.min(1, Math.max(0, (currentTimeRef.current - segStart) / segDuration));
    };
    const getBaseZoom = (t) => startZoom + (endZoom - startZoom) * t;

    const initialT = getSegmentT();
    const initialZoom = getBaseZoom(initialT);
    camera.position.set(
      camStart.x + (camEnd.x - camStart.x) * initialT,
      camStart.y + (camEnd.y - camStart.y) * initialT,
      Math.max(minZoom, Math.min(maxZoom, initialZoom))
    );
    camera.rotation.set(
      rotStart.x + (rotEnd.x - rotStart.x) * initialT,
      rotStart.y + (rotEnd.y - rotStart.y) * initialT,
      rotStart.z + (rotEnd.z - rotStart.z) * initialT
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, initialQuality.pixelRatioCap));
    container.appendChild(renderer.domElement);

    const composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene3d, camera);
    composer.addPass(renderPass);

    const hBlurPass = new ShaderPass(HorizontalBlurShader);
    const vBlurPass = new ShaderPass(VerticalBlurShader);
    composer.addPass(hBlurPass);
    composer.addPass(vBlurPass);

    const bloomPass = new UnrealBloomPass(new THREE.Vector2(width, height), 0, 0.6, 0.85);
    composer.addPass(bloomPass);

    const brightnessContrastPass = new ShaderPass(BrightnessContrastShader);
    composer.addPass(brightnessContrastPass);

    const hueSaturationPass = new ShaderPass(HueSaturationShader);
    composer.addPass(hueSaturationPass);

    const vignettePass = new ShaderPass(VignetteShader);
    composer.addPass(vignettePass);

    const ambient = new THREE.AmbientLight(0xffffff, 0.3);
    scene3d.add(ambient);

    const tunnelGroup = new THREE.Group();
    const tunnelRings = [];
    const ringPoints = points;
    const ringSpacing = 1;
    let baseRadius = 0;

    const updateBaseRadius = () => {
      const halfHeight = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) * camera.position.z;
      const halfWidth = halfHeight * camera.aspect;
      baseRadius = Math.min(halfWidth, halfHeight) * 1.1;
    };
    updateBaseRadius();

    const angleOffset = -Math.PI / 2;
    for (let r = 0; r < ringCount; r += 1) {
      const geo = new LineGeometry();
      const ringPos = new Float32Array((ringPoints + 1) * 3);
      for (let i = 0; i <= ringPoints; i += 1) {
        ringPos[i * 3] = 0;
        ringPos[i * 3 + 1] = 0;
        ringPos[i * 3 + 2] = 0;
      }
      geo.setPositions(ringPos);
      const hue = (r / ringCount) % 1;
      const col = new THREE.Color().setHSL(hue, 0.8, 0.55);
      const mat = new LineMaterial({
        color: col,
        transparent: true,
        opacity: activeSegment.opacityStart ?? 0.8,
        linewidth: baseLineWidth,
      });
      mat.resolution.set(width, height);
      const line = new Line2(geo, mat);
      line.position.z = camera.position.z - r * ringSpacing;
      const tiltX = (Math.random() - 0.5) * Math.PI * baseRandomness;
      const tiltY = (Math.random() - 0.5) * Math.PI * baseRandomness;
      line.rotation.x = tiltX;
      line.rotation.y = tiltY;
      tunnelGroup.add(line);
      const ringRandom = new Float32Array(ringPoints);
      for (let i = 0; i < ringPoints; i += 1) ringRandom[i] = (Math.random() - 0.5) * 2;
      tunnelRings.push({ line, geometry: geo, positions: ringPos, material: mat, ringPoints, ringRandom });
    }
    scene3d.add(tunnelGroup);

    let frameId = null;
    let lastTime = performance.now();

    const ensureAnalyser = (quality) => {
      if (!audio || audio.paused) return;
      if (analyserReadyAtRef.current > 0 && performance.now() < analyserReadyAtRef.current) return;
      if (analyserRef.current) {
        if (analyserRef.current.fftSize !== quality.fftSize) {
          analyserRef.current.fftSize = quality.fftSize;
          dataRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
          freqRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
        }
        return;
      }
      try {
        const ctx = ctxRef.current ?? new (window.AudioContext || window.webkitAudioContext)();
        ctxRef.current = ctx;
        const source = sourceRef.current ?? ctx.createMediaElementSource(audio);
        sourceRef.current = source;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = quality.fftSize;
        analyser.smoothingTimeConstant = 0.85;
        source.connect(analyser);
        analyser.connect(ctx.destination);
        analyserRef.current = analyser;
        dataRef.current = new Uint8Array(analyser.frequencyBinCount);
        freqRef.current = new Uint8Array(analyser.frequencyBinCount);
      } catch {
        // ignore
      }
    };

    const handlePlay = () => {
      const ctx = ctxRef.current;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
    };

    if (audio) {
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('playing', handlePlay);
    }

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const now = performance.now();
      const minFrameTime = 1000 / Math.max(1, targetFpsRef.current || 60);
      if (now - lastTime < minFrameTime) return;
      const dt = Math.min(0.05, (now - lastTime) * 0.001);
      lastTime = now;

      const perf = perfRef.current;
      perf.samples[perf.idx] = dt;
      perf.idx = (perf.idx + 1) % perf.samples.length;
      if (now - perf.lastEval > 1000) {
        const avg = perf.samples.reduce((a, b) => a + b, 0) / perf.samples.length;
        const fps = 1 / Math.max(avg, 0.0001);
        const currentTier = qualityTierRef.current;
        if (fps < 32 && now - perf.lastChange > 2000) {
          const next = currentTier === 'high' ? 'medium' : 'low';
          if (next !== currentTier) {
            perf.lastChange = now;
            setQualityTier(next);
          }
        } else if (fps > 55 && now - perf.lastChange > 6000) {
          const next = currentTier === 'low' ? 'medium' : 'high';
          if (next !== currentTier) {
            perf.lastChange = now;
            setQualityTier(next);
          }
        }
        perf.lastEval = now;
      }

      const currentQuality = getQualityProfile(qualityTierRef.current);
      const runtime = qualityRuntimeRef.current;
      if (runtime.lastTier !== qualityTierRef.current || runtime.lastPixelRatioCap !== currentQuality.pixelRatioCap) {
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, currentQuality.pixelRatioCap));
        runtime.lastTier = qualityTierRef.current;
        runtime.lastPixelRatioCap = currentQuality.pixelRatioCap;
      }

      if (!active) {
        if (currentQuality.postFX) composer.render();
        else renderer.render(scene3d, camera);
        return;
      }

      const motionDiff = motionTargetRef.current - motionScaleRef.current;
      const transitionSeconds = Math.max(motionTransitionSecondsRef.current, 0.001);
      const maxStep = (MotionScaleMax - MotionScaleMin) * (dt / transitionSeconds);
      if (Math.abs(motionDiff) <= maxStep) {
        motionScaleRef.current = motionTargetRef.current;
      } else {
        motionScaleRef.current += Math.sign(motionDiff) * maxStep;
      }
      const motionScale = clampValue(motionScaleRef.current, MotionScaleMin, MotionScaleMax, MotionScaleMax);

      ensureAnalyser(currentQuality);

      const analyser = analyserRef.current;
      const data = dataRef.current;
      const freq = freqRef.current;
      if (analyser && data) analyser.getByteTimeDomainData(data);
      if (analyser && freq) analyser.getByteFrequencyData(freq);

      const farZ = camera.position.z - ringCount * ringSpacing;

      const segmentT = getSegmentT();
      const lerp = (a, b) => a + (b - a) * segmentT;
      const speed = lerp(baseSpeed, nextSegment.speed ?? baseSpeed) * motionScale;
      const lineWidth = lerp(baseLineWidth, nextSegment.lineWidth ?? baseLineWidth);
      const ringAmpScale = lerp(baseRingAmpScale, nextSegment.ringAmpScale ?? baseRingAmpScale);
      const amplitudeScale = lerp(baseAmplitudeScale, nextSegment.amplitude ?? baseAmplitudeScale) * motionScale;
      const randomness = lerp(baseRandomness, nextSegment.randomness ?? baseRandomness);
      const rotationSpeed = lerp(baseRotationSpeed, nextSegment.rotationSpeed ?? baseRotationSpeed) * motionScale;
      const originX = lerp(originStart.x ?? 0, originEnd.x ?? originStart.x ?? 0);
      const originY = lerp(originStart.y ?? 0, originEnd.y ?? originStart.y ?? 0);
      const centerYOffset = lerp(activeSegment.centerYOffsetStart ?? 0, activeSegment.centerYOffsetEnd ?? activeSegment.centerYOffsetStart ?? 0);
      const baseZoom = getBaseZoom(segmentT);
      const desiredZoom = Math.max(minZoom, Math.min(maxZoom, baseZoom + zoomOffsetRef.current));
      zoomOffsetRef.current = desiredZoom - baseZoom;
      camera.position.x = camStart.x + (camEnd.x - camStart.x) * segmentT;
      camera.position.y = camStart.y + (camEnd.y - camStart.y) * segmentT;
      const targetRotX = rotStart.x + (rotEnd.x - rotStart.x) * segmentT;
      const targetRotY = rotStart.y + (rotEnd.y - rotStart.y) * segmentT;
      const targetRotZ = rotStart.z + (rotEnd.z - rotStart.z) * segmentT;
      const rotSmooth = 1 - Math.exp(-dt * 6);
      camera.rotation.x = THREE.MathUtils.lerp(camera.rotation.x, targetRotX, rotSmooth);
      camera.rotation.y = THREE.MathUtils.lerp(camera.rotation.y, targetRotY, rotSmooth);
      camera.rotation.z = THREE.MathUtils.lerp(camera.rotation.z, targetRotZ, rotSmooth);
      if (Math.abs(camera.position.z - desiredZoom) > 0.0001) {
        camera.position.z = desiredZoom;
        updateBaseRadius();
      }

      const blurStart = activeSegment.blurStart ?? 0;
      const blurEnd = activeSegment.blurEnd ?? blurStart;
      const bloomStart = activeSegment.bloomStart ?? 0;
      const bloomEnd = activeSegment.bloomEnd ?? bloomStart;
      const brightnessStart = activeSegment.brightnessStart ?? 0;
      const brightnessEnd = activeSegment.brightnessEnd ?? brightnessStart;
      const contrastStart = activeSegment.contrastStart ?? 0;
      const contrastEnd = activeSegment.contrastEnd ?? contrastStart;
      const saturationStart = activeSegment.saturationStart ?? 0;
      const saturationEnd = activeSegment.saturationEnd ?? saturationStart;

      const blurAmount = (blurStart + (blurEnd - blurStart) * segmentT) * motionScale;
      const bloomAmount = (bloomStart + (bloomEnd - bloomStart) * segmentT) * motionScale;
      const brightness = (brightnessStart + (brightnessEnd - brightnessStart) * segmentT) * motionScale;
      const contrast = (contrastStart + (contrastEnd - contrastStart) * segmentT) * motionScale;
      const saturation = (saturationStart + (saturationEnd - saturationStart) * segmentT) * motionScale;

      const blurEnabled = blurAmount > 0.001;
      hBlurPass.enabled = blurEnabled;
      vBlurPass.enabled = blurEnabled;
      if (blurEnabled) {
        hBlurPass.uniforms.h.value = blurAmount / width;
        vBlurPass.uniforms.v.value = blurAmount / height;
      }

      bloomPass.enabled = bloomAmount > 0.001;
      bloomPass.strength = bloomAmount;

      const bcEnabled = Math.abs(brightness) > 0.001 || Math.abs(contrast) > 0.001;
      brightnessContrastPass.enabled = bcEnabled;
      if (bcEnabled) {
        brightnessContrastPass.uniforms.brightness.value = brightness;
        brightnessContrastPass.uniforms.contrast.value = contrast;
      }

      const satEnabled = Math.abs(saturation) > 0.001;
      hueSaturationPass.enabled = satEnabled;
      if (satEnabled) {
        hueSaturationPass.uniforms.hue.value = 0;
        hueSaturationPass.uniforms.saturation.value = saturation;
      }

      vignettePass.enabled = false;

      let low = 0;
      let mid = 0;
      let high = 0;
      if (freq && freq.length) {
        const len = freq.length;
        for (let i = 0; i < len; i += 1) {
          const v = freq[i] / 255;
          if (i < len * 0.12) low += v;
          else if (i < len * 0.35) mid += v;
          else high += v;
        }
        low /= len * 0.12;
        mid /= len * 0.23;
        high /= len * 0.65;
      }
      const overallAmp = (low + mid + high) / 3;
      const lowMid = low * 1.1 + mid * 0.7;
      const closeRing = overallAmp < 0.05 && Math.abs(speed) < 0.01 && Math.abs(randomness) < 0.01;

      runtime.frame += 1;
      const shouldUpdateGeometry = currentQuality.frameSkip <= 1 || runtime.frame % currentQuality.frameSkip === 0;
      const isPolygonShape = shape === 'polygon' || shape === 'hexagon' || shape === 'triangle' || shape === 'square';
      const polygonSides = shape === 'triangle' ? 3 : shape === 'square' ? 4 : 6;
      tunnelRings.forEach(({ line, geometry, positions: ringPos, material, ringPoints: rp, ringRandom }, idx) => {
        if (rotationSpeed) {
          line.rotation.z += rotationSpeed * dt;
        }
        line.position.z += speed * dt;
        if (line.position.z > camera.position.z + 6) {
          line.position.z = farZ;
        }
        const depthNorm = 1 - Math.min(1, Math.max(0, (line.position.z - farZ) / (0 - farZ)));
        line.position.x = originX + (camera.position.x - originX) * depthNorm;
        line.position.y = originY + ((camera.position.y + centerYOffset) - originY) * depthNorm;
        const lowWeight = 1 - depthNorm; // near camera
        const highWeight = depthNorm; // far away
        const bandAmp = (lowWeight * lowMid + highWeight * high + overallAmp * 0.15) * amplitudeScale;
        if (shouldUpdateGeometry) {
          const ringRadius = baseRadius + bandAmp * ringAmpScale;
          for (let i = 0; i < rp; i += 1) {
            const t = closeRing && i === rp - 1 ? 1 : i / rp;
            const angle = angleOffset + t * Math.PI * 2;
            const wave = data ? (data[i % data.length] / 128 - 1) : 0;
            const jitter = ringRandom ? ringRandom[i] * randomness * baseRadius * 0.05 : 0;
            const audioOffset = wave * ringAmpScale * 2.2;
            const baseShape = isPolygonShape ? polygonRadius(angle, polygonSides) : shapeRadius(angle, shape, amplitudeScale, wave);
            const radius = ringRadius * baseShape + audioOffset + jitter;
            ringPos[i * 3] = Math.cos(angle) * radius;
            ringPos[i * 3 + 1] = Math.sin(angle) * radius;
            ringPos[i * 3 + 2] = 0;
          }
          ringPos[rp * 3] = ringPos[0];
          ringPos[rp * 3 + 1] = ringPos[1];
          ringPos[rp * 3 + 2] = ringPos[2];
          geometry.setPositions(ringPos);
        }
        const hueShift = (idx / ringCount + now * 0.00005) % 1;
        material.color.setHSL(hueShift, 0.8, 0.55);
        material.linewidth = lineWidth;
        material.opacity = clampValue((activeSegment.opacityStart ?? 0.8) * motionScale, 0.02, 1, 0.08);
      });

      if (currentQuality.postFX) composer.render();
      else renderer.render(scene3d, camera);
    };

    const onResize = () => {
      const { width: w, height: h } = getSize();
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
      updateBaseRadius();
      tunnelRings.forEach(({ material }) => {
        material.resolution.set(w, h);
      });
    };
    window.addEventListener('resize', onResize);
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);

    animate();

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
      resizeObserver.disconnect();
      if (audio) {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('playing', handlePlay);
      }
      renderer.dispose();
      if (renderer.forceContextLoss) {
        try {
          renderer.forceContextLoss();
        } catch {
          // ignore
        }
      }
      tunnelRings.forEach(({ line }) => {
        line.geometry.dispose();
        line.material.dispose();
      });
      if (renderer.domElement && renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [audio, segmentIndex, segments, active]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

export default MusicVisualizer;
